import storage from 'redux-persist/lib/storage';
import hardSet from 'redux-persist/lib/stateReconciler/hardSet';
import {
  combineReducers,
  configureStore,
  type Reducer,
  type UnknownAction
} from '@reduxjs/toolkit';
import type { PersistPartial } from 'redux-persist/es/persistReducer';
import {
  persistReducer,
  persistStore,
  type PersistConfig
} from 'redux-persist';
import { appReducer } from '@/store/app/app.reducer.ts';
import { setupListeners } from '@reduxjs/toolkit/query';
import { encryptTransform } from 'redux-persist-transform-encrypt';
import { apiService } from '@services/api.service.ts';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

// Derive the app state shape from the combined app reducer
export type AppState = ReturnType<typeof appReducer>;

const persistConfig: PersistConfig<AppState> = {
  keyPrefix: 'pro:',
  key: 'pro',
  storage: storage,
  stateReconciler: hardSet,
  whitelist: ['auth', 'config'],
  debug: import.meta.env.MODE === 'development',
  transforms: [
    encryptTransform({
      secretKey: '336205c57461932b98e543c228f236fb077', // Move and update this key in .env
      onError: function (error) {
        console.log('Critical Error Transforming Encrypted Data ==>', error);
      }
    })
  ]
};

// Persisted app reducer
const persistedAppReducer = persistReducer(
  persistConfig,
  appReducer
) as Reducer<AppState & PersistPartial, UnknownAction>;

// Root reducer for the store
const rootReducer = combineReducers({
  app: persistedAppReducer,
  [apiService.reducerPath]: apiService.reducer
});

// Export RootState based on rootReducer (avoid referencing store here to prevent circular types)
export type RootState = ReturnType<typeof rootReducer>;

export const store = configureStore({
  devTools: import.meta.env.MODE === 'development',
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({ serializableCheck: false }).concat(
      apiService.middleware as any
    )
});

setupListeners(store.dispatch);

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
